import { useState } from "react";
import { useParams } from "react-router";
import { useActividadesAprendiz } from "@/hooks/actividadesAprendiz/useActividadesAprendiz";
import { useActualizarActividadAprendiz } from "@/hooks/actividadesAprendiz/useActualizarActividadAprendiz";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";

export default function ActividadesAprendiz() {
  const { idAprendizPrograma, idPrograma } = useParams<{
    idAprendizPrograma: string;
    idPrograma: string;
  }>();

  const aprendizId = idAprendizPrograma
    ? parseInt(idAprendizPrograma)
    : undefined;
  const programaId = idPrograma ? parseInt(idPrograma) : undefined;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedActividad, setSelectedActividad] = useState<{
    id: number;
    idActividadPrograma: number;
    intentosActuales: number;
    intentosExtrasActuales: number;
    nombre: string;
  } | null>(null);
  const [intentosExtras, setIntentosExtras] = useState<number>(0);
  const [accionIntentos, setAccionIntentos] = useState<"sumar" | "restar">(
    "sumar"
  );

  const {
    data: actividades,
    isLoading,
    error,
  } = useActividadesAprendiz(aprendizId, programaId);

  const { mutateAsync: actualizarActividad, isPending } =
    useActualizarActividadAprendiz(programaId);

  const handleAsignarIntentos = (
    actividadId: number,
    idActividadPrograma: number,
    intentosActuales: number,
    intentosExtrasActuales: number,
    nombre: string,
    accion: "sumar" | "restar" = "sumar"
  ) => {
    setSelectedActividad({
      id: actividadId,
      idActividadPrograma,
      intentosActuales,
      intentosExtrasActuales,
      nombre,
    });
    setAccionIntentos(accion);
    setIntentosExtras(0);
    setDialogOpen(true);
  };

  const handleConfirmarAsignacion = async () => {
    if (!selectedActividad || !aprendizId) return;

    try {
      const nuevoIntentosExtras =
        accionIntentos === "restar"
          ? (selectedActividad.intentosExtrasActuales || 0) - intentosExtras
          : (selectedActividad.intentosExtrasActuales || 0) + intentosExtras;

      await actualizarActividad({
        actividadAprendizId: selectedActividad.id,
        idAprendiz: aprendizId,
        idActividad: selectedActividad.idActividadPrograma,
        intentosExtras: nuevoIntentosExtras < 0 ? 0 : nuevoIntentosExtras,
      });
      toast.success(
        accionIntentos === "restar"
          ? "Intentos extras disminuidos correctamente."
          : "Intentos extras asignados correctamente."
      );
    } catch (error) {
      toast.error("No se pudieron actualizar los intentos extras.");
    }

    setDialogOpen(false);
    setSelectedActividad(null);
    setIntentosExtras(0);
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-600">Error al cargar las actividades</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const actividadesCompletadas =
    actividades?.filter(
      (a) => a.actividadAprendiz && a.actividadAprendiz.intentos > 0
    ).length || 0;

  const totalActividades = actividades?.length || 0;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Actividades del Aprendiz</h1>
          <p className="text-muted-foreground mt-1">
            Progreso: {actividadesCompletadas} de {totalActividades} actividades
            completadas
          </p>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          {totalActividades > 0
            ? Math.round((actividadesCompletadas / totalActividades) * 100)
            : 0}
          % Completado
        </Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Actividades</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead className="text-center">Intentos</TableHead>
                <TableHead className="text-center">
                  Interacciones Chat
                </TableHead>
                <TableHead className="text-center">Última Actividad</TableHead>
                <TableHead className="text-center">Estado</TableHead>
                <TableHead className="text-center">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {actividades && actividades.length > 0 ? (
                actividades.map((actividad) => {
                  const intentosUsados =
                    actividad.actividadAprendiz?.intentos || 0;
                  const intentosRestantes =
                    actividad.actividadAprendiz?.intentosRestantes ||
                    actividad.limiteIntentos;
                  const ultimaActividad =
                    actividad.actividadAprendiz?.ultimoIntento?.fechaIntento;
                  const tieneRetro =
                    actividad.actividadAprendiz?.ultimoIntento?.tieneRetro;

                  return (
                    <TableRow key={actividad.idActividadPrograma}>
                      <TableCell className="font-medium">
                        {actividad.nombre}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Badge
                            variant={
                              intentosRestantes === 0
                                ? "destructive"
                                : "secondary"
                            }
                          >
                            {intentosUsados} /{" "}
                            {actividad.limiteIntentos +
                              (actividad.actividadAprendiz?.intentosExtras ||
                                0)}
                          </Badge>
                          {intentosRestantes > 0 && (
                            <span className="text-xs text-muted-foreground">
                              ({intentosRestantes} restantes)
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">
                          {actividad.actividadAprendiz?.numInteraccionesChat ||
                            0}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {ultimaActividad ? (
                          <div className="flex flex-col gap-1">
                            <span>
                              {new Date(ultimaActividad).toLocaleDateString(
                                "es-ES",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                }
                              )}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(ultimaActividad).toLocaleTimeString(
                                "es-ES",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">
                            Sin actividad
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {!actividad.actividadAprendiz ? (
                          <Badge variant="secondary">No iniciado</Badge>
                        ) : intentosUsados === 0 ? (
                          <Badge variant="secondary">Sin intentos</Badge>
                        ) : tieneRetro ? (
                          <Badge variant="default" className="bg-green-500">
                            Completado
                          </Badge>
                        ) : intentosRestantes === 0 ? (
                          <Badge variant="destructive">Sin intentos</Badge>
                        ) : (
                          <Badge variant="secondary">En progreso</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8"
                          title="Aumentar intentos extras"
                          aria-label="Aumentar intentos extras"
                          onClick={() =>
                            handleAsignarIntentos(
                              actividad.actividadAprendiz?.id || 0,
                              actividad.idActividadPrograma,
                              actividad.actividadAprendiz?.intentos || 0,
                              actividad.actividadAprendiz?.intentosExtras || 0,
                              actividad.nombre,
                              "sumar"
                            )
                          }
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="secondary"
                          className="h-8 w-8"
                          title="Disminuir intentos extras"
                          aria-label="Disminuir intentos extras"
                          onClick={() =>
                            handleAsignarIntentos(
                              actividad.actividadAprendiz?.id || 0,
                              actividad.idActividadPrograma,
                              actividad.actividadAprendiz?.intentos || 0,
                              actividad.actividadAprendiz?.intentosExtras || 0,
                              actividad.nombre,
                              "restar"
                            )
                          }
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No hay actividades disponibles
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {accionIntentos === "restar"
                ? "Disminuir intentos extras"
                : "Aumentar intentos extras"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                Actividad:{" "}
                <span className="font-medium">{selectedActividad?.nombre}</span>
              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Número de intentos extras
              </label>
              <Input
                type="number"
                min="0"
                value={intentosExtras}
                onChange={(e) =>
                  setIntentosExtras(parseInt(e.target.value) || 0)
                }
                placeholder="Ingresa el número de intentos"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleConfirmarAsignacion} disabled={isPending}>
              {isPending
                ? "Actualizando..."
                : accionIntentos === "restar"
                ? "Disminuir intentos"
                : "Aumentar intentos"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
